import { useEffect, useRef } from "react";

const AmbientBackground = ({ className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    if (!canvas || !host) return undefined;

    const context = canvas.getContext("2d", { alpha: true });
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let particles = [];
    let frame = 0;
    let visible = true;
    let lastTime = 0;

    const draw = () => {
      context.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((particle) => {
        context.beginPath();
        context.fillStyle = particle.gold
          ? `rgba(213, 189, 145, ${Math.min(0.13, particle.alpha * 1.3)})`
          : `rgba(255, 255, 255, ${particle.alpha})`;
        context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        context.fill();
      });
    };

    const resize = () => {
      const rect = host.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.max(1, Math.round(rect.width * ratio));
      canvas.height = Math.max(1, Math.round(rect.height * ratio));
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      const count = Math.min(42, Math.max(14, Math.round((rect.width * rect.height) / 31000)));
      particles = Array.from({ length: count }, (_, index) => ({
        x: Math.random() * rect.width,
        y: Math.random() * rect.height,
        radius: 0.5 + Math.random() * 0.8,
        alpha: 0.04 + Math.random() * 0.06,
        speed: 0.012 + Math.random() * 0.022,
        gold: index % 9 === 0,
      }));
      draw();
    };

    const animate = (time) => {
      if (!visible || reducedMotion.matches) return;
      const delta = Math.min(32, time - lastTime || 16.7);
      lastTime = time;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      particles.forEach((particle) => {
        particle.x += particle.speed * delta;
        particle.y += particle.speed * delta * 0.42;
        if (particle.x > width + 2) particle.x = -2;
        if (particle.y > height + 2) particle.y = -2;
      });
      draw();
      frame = requestAnimationFrame(animate);
    };

    const syncAnimation = () => {
      cancelAnimationFrame(frame);
      draw();
      if (visible && !reducedMotion.matches) {
        lastTime = performance.now();
        frame = requestAnimationFrame(animate);
      }
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      syncAnimation();
    }, { rootMargin: "120px" });
    const resizeObserver = new ResizeObserver(resize);
    const onMotionChange = () => syncAnimation();

    resize();
    observer.observe(host);
    resizeObserver.observe(host);
    reducedMotion.addEventListener("change", onMotionChange);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      resizeObserver.disconnect();
      reducedMotion.removeEventListener("change", onMotionChange);
    };
  }, []);

  return <canvas ref={canvasRef} className={`ambient-background ${className}`} aria-hidden="true" />;
};

export default AmbientBackground;
